import React from 'react';

import { FormattedMessage } from 'react-intl';

import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';
import FlagGroup from '@atlaskit/flag/flag-group';
import Tick from '@atlaskit/icon/core/check-circle';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FLAG_DISMISSED } from '../../common/constants';
import { type InteractedUIEventAction } from '../../common/types';
import { useACTAnalyticsEvents } from '../../common/utils';
import { useAdControlProvider } from '../../controllers';
import { messages } from '../messages';
import { overridePortalIndex } from '../utils';

const ACT_FLAG_WRAPPER = 'act-flag-wrapper';

const interactedUIEventAction: InteractedUIEventAction = {
	action: 'clicked',
	actionSubject: 'button',
	actionSubjectId: 'adControlsFlagClose',
};

const DismissalFlagGroup: React.FunctionComponent = () => {
	overridePortalIndex(ACT_FLAG_WRAPPER);
	const {
		state: { flags },
		createEventDispatcher,
	} = useAdControlProvider();
	const { createInteractedUIEvent } = useACTAnalyticsEvents();

	const handleOnDismissed = React.useCallback(
		(id: number | string) => {
			createEventDispatcher(id).dispatch(
				FLAG_DISMISSED,
				createInteractedUIEvent(interactedUIEventAction),
			);
		},
		[createEventDispatcher, createInteractedUIEvent],
	);

	return (
		<FlagGroup onDismissed={handleOnDismissed}>
			{Array.from(flags.entries()).map(
				([itemId, { actions = [], ...flagProps } = { actions: [] }], i) => (
					<Box key={i} id={ACT_FLAG_WRAPPER}>
						<AutoDismissFlag
							id={itemId}
							testId={`act-flag-${itemId}`}
							icon={<Tick label="Success" spacing="spacious" color={token('color.icon.success')} />}
							title={<FormattedMessage {...messages.notInterestedFlag} />}
							description={<FormattedMessage {...messages.dismissalMessage} />}
							{...flagProps}
							actions={actions.map((action) => ({
								...action,
								onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
									action.onClick?.(e, () => handleOnDismissed(itemId));
								},
							}))}
						/>
					</Box>
				),
			)}
		</FlagGroup>
	);
};

export default DismissalFlagGroup;
